import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { version } from '../../../version';
import { THEMES } from '../../data/product/product.data';
import { DarkLightSettings, DEFAULT_THEME, PRODUCT_THEME, ThemeColorSettings } from '../../data/theme/theme.data';
import { Product } from '../../model/product/product.model';

/** A product theme picked from the Appearance menu, its base colors feed the
 *  derived ramp (via inline custom properties on the root element). `category`
 *  (nature/cities) is kept so the theme's palette.json can be re-fetched on
 *  reload to restore the IDE skin. */
export interface ProductThemeSelection {
  key: string;
  primary: string;
  secondary?: string;
  tertiary?: string;
  category?: string;
}

/** Structural surface colors pulled from a JetBrains theme's palette.json,
 *  mapped onto the site's core surfaces (page background, cards, top-nav,
 *  body text) when the "IDE skin" is active. Null when no theme is active or
 *  the theme ships no bundled palette. */
export interface IdeSurfaceTokens {
  editorBackground: string;
  editorForeground: string;
  windowBackground: string;
  surface: string;
  /** Text-field / combo-box fill (JetBrains `inputBackground`). Falls back to
   *  windowBackground when a palette omits it. */
  inputBackground: string;
  primaryForeground: string;
  /** Dim/secondary text. Optional in some palettes, empty falls back to a
   *  derived dim tone in codigrate.color.theme.scss. */
  secondaryForeground: string;
  /** Plugin primary color. Drives the site accent ramp (--theme-color). */
  pluginPrimary: string;
  /** Plugin secondary color. Drives the site secondary accent ramp (--theme-color-secondary). */
  pluginSecondary: string;
  /** Tertiary color. Drives the site tertiary accent ramp (--theme-color-tertiary). */
  tertiaryColor: string;
  /** Hover/selection background (JetBrains `hoverBackground`). */
  hover: string;
  /** Appearance: 'Light' | 'Dark' | 'Light & Dark' */
  appearance?: string;
  /** Semantic state colours → site --error/--warning/--info/--success. */
  error: string;
  warning: string;
  info: string;
  success: string;
}

@Injectable( {
  providedIn: 'root'
} )
export class ThemeService {

  public appVersion = '1.0.0';
  public darkLightSetting: Observable<string>;
  public themeColorSetting: Observable<string>;
  public themeDesignSetting: Observable<string>;
  public sideNavClosed: Observable<string>;
  public productTheme: Observable<ProductThemeSelection | null>;
  public productSurface: Observable<IdeSurfaceTokens | null>;

  private darkLightSettingSubject: BehaviorSubject<string>;
  private themeColorSettingSubject: BehaviorSubject<string>;
  private themeDesignSettingSubject: BehaviorSubject<string>;
  private sideNavClosedSubject: BehaviorSubject<string>;
  private productThemeSubject: BehaviorSubject<ProductThemeSelection | null>;
  private productSurfaceSubject: BehaviorSubject<IdeSurfaceTokens | null>;

  constructor( private http: HttpClient ) {
    let themeColorSettingStorageItem = localStorage.getItem( 'themeColorSetting' ) ?
        localStorage.getItem( 'themeColorSetting' ) : sessionStorage.getItem( 'themeColorSetting' );
    themeColorSettingStorageItem = themeColorSettingStorageItem ? themeColorSettingStorageItem : DEFAULT_THEME;

    this.themeColorSettingSubject = new BehaviorSubject<string>( themeColorSettingStorageItem );
    this.themeColorSetting = this.themeColorSettingSubject.asObservable();

    let themeDesignSettingStorageItem = localStorage.getItem( 'themeDesignSetting' ) ?
        localStorage.getItem( 'themeDesignSetting' ) : sessionStorage.getItem( 'themeDesignSetting' );
    themeDesignSettingStorageItem = themeDesignSettingStorageItem ? themeDesignSettingStorageItem : DEFAULT_THEME;

    this.themeDesignSettingSubject = new BehaviorSubject<string>( themeDesignSettingStorageItem );
    this.themeDesignSetting = this.themeDesignSettingSubject.asObservable();

    let darkLightSettingStorageItem = localStorage.getItem( 'darkLightSetting' ) ?
        localStorage.getItem( 'darkLightSetting' ) : sessionStorage.getItem( 'darkLightSetting' );
    darkLightSettingStorageItem = darkLightSettingStorageItem ? darkLightSettingStorageItem : 'auto';

    this.darkLightSettingSubject = new BehaviorSubject<string>( darkLightSettingStorageItem );
    this.darkLightSetting = this.darkLightSettingSubject.asObservable();

    let sideNavClosedStorageItem = localStorage.getItem( 'sideNavClosed' ) ?
        localStorage.getItem( 'sideNavClosed' ) : sessionStorage.getItem( 'sideNavClosed' );
    sideNavClosedStorageItem = sideNavClosedStorageItem ? sideNavClosedStorageItem : 'auto';

    this.sideNavClosedSubject = new BehaviorSubject<string>( sideNavClosedStorageItem );
    this.sideNavClosed = this.sideNavClosedSubject.asObservable();

    this.sideNavClosedSubject = new BehaviorSubject<string>( sideNavClosedStorageItem );
    this.sideNavClosed = this.sideNavClosedSubject.asObservable();

    const productThemeStorageItem = localStorage.getItem( 'productTheme' ) ?
        localStorage.getItem( 'productTheme' ) : sessionStorage.getItem( 'productTheme' );
    let productThemeParsed: ProductThemeSelection | null = null;
    try {
      productThemeParsed = productThemeStorageItem ? JSON.parse( productThemeStorageItem ) : null;
    } catch {
      productThemeParsed = null;
    }
    this.productThemeSubject = new BehaviorSubject<ProductThemeSelection | null>( productThemeParsed );
    this.productTheme = this.productThemeSubject.asObservable();

    this.productSurfaceSubject = new BehaviorSubject<IdeSurfaceTokens | null>( null );
    this.productSurface = this.productSurfaceSubject.asObservable();

    // The surface tokens for the IDE skin are now bundled directly within
    // the product data. Restore them on reload if a product theme is selected.
    if ( productThemeParsed?.key ) {
      let foundProduct: Product | undefined;
      for ( const productLine of THEMES ) {
        foundProduct = productLine.products.find( p => p.key === productThemeParsed.key );
        if ( foundProduct ) {
          break;
        }
      }

      if ( foundProduct ) {
        this.loadDesignTokens( foundProduct.designTokens, foundProduct.appearance );
      }
    }

    if ( version ) {
      this.appVersion = version;
    }
  }

  setDarkLightSetting( darkLightSetting: string ) {
    this.persistThemeItem( 'darkLightSetting', darkLightSetting );
    this.darkLightSettingSubject.next( darkLightSetting );
  }

  setThemeColorSetting( themeColorSetting: string ) {
    this.persistThemeItem( 'themeColorSetting', themeColorSetting );
    this.themeColorSettingSubject.next( themeColorSetting );
  }

  setThemeDesignSetting( themeDesignSetting: string ) {
    this.persistThemeItem( 'themeDesignSetting', themeDesignSetting );
    this.themeDesignSettingSubject.next( themeDesignSetting );
  }

  /**
   * Applies a product theme (Aurora, Everest, ...): its colors drive the
   * derived ramp via the `product-theme` color class, and the site flips to
   * the theme's intended appearance, a Dark theme switches dark mode on, a
   * Light theme switches it off. Dual ('Light & Dark') themes leave the
   * current mode untouched.
   */
  setProductTheme( product: Product ) {
    const selection: ProductThemeSelection = {
      key: product.key,
      primary: product.designTokens?.primaryColor,
      secondary: product.designTokens?.secondaryColor,
      tertiary: product.designTokens?.tertiaryColor,
      category: this.deriveCategory( product.url ),
    };

    this.persistThemeItem( 'productTheme', JSON.stringify( selection ) );
    this.productThemeSubject.next( selection );
    this.loadDesignTokens( product.designTokens, product.appearance );
    this.setThemeColorSetting( PRODUCT_THEME );

    const appearance = ( product.appearance || '' ).toLowerCase();
    const isDark = appearance.includes( 'dark' );
    const isLight = appearance.includes( 'light' );
    if ( isDark && !isLight ) {
      this.setDarkLightSetting( DarkLightSettings.dark );
    } else if ( isLight && !isDark ) {
      this.setDarkLightSetting( DarkLightSettings.light );
    }
  }

  /** Clears any product theme and restores the site's default (blue) colors.
   *  Leaves the dark/light mode as the visitor left it. */
  resetThemeColor() {
    localStorage.removeItem( 'productTheme' );
    sessionStorage.removeItem( 'productTheme' );
    this.productThemeSubject.next( null );
    this.productSurfaceSubject.next( null );
    this.setThemeColorSetting( DEFAULT_THEME );
  }

  /** Persists a theme preference to localStorage so the selection survives
   *  across sessions and tabs (not just the current tab/session). Any stale
   *  session-scoped value from older builds is dropped. */
  private persistThemeItem( key: string, value: string ) {
    sessionStorage.removeItem( key );
    localStorage.setItem( key, value );
  }

  /** Derives the theme category (e.g. `nature`, `cities`) from a product url
   *  that ends with `.../<category>/<key>-theme`. Empty when not derivable,    *  the IDE skin is then skipped (the site keeps its default surfaces). */
  private deriveCategory( url: string | undefined ): string {
    if ( !url ) {
      return '';
    }
    const match = url.match( /\/([^/]+)\/[^/]+-theme\/?$/ );
    return match ? match[ 1 ] : '';
  }

  /**
   * Fetches the selected theme's bundled palette.json and publishes its
   * structural surface tokens (editor / window / surface backgrounds + the
   * primary foreground). These feed the site-wide "IDE skin" via inline custom
   * properties on the root element (see AppComponent + codigrate.color.theme.scss).
   *
   * Themes that ship no bundled palette (or any fetch error / incomplete
   * token set) clear the skin, so the site falls back to its default surfaces.
   */
  private loadDesignTokens( designTokens?: any, appearance?: string ): void {
    if ( designTokens ) {
      this.publishDesignTokens( designTokens, appearance );
    } else {
      this.productSurfaceSubject.next( null );
    }
  }

  private publishDesignTokens( tokens: any, appearance?: string ): void {
    const editorBackground = tokens.editorBackground ?? '';
    const editorForeground = tokens.editorForeground ?? '';
    const windowBackground = tokens.windowBackground ?? '';
    const primaryForeground = tokens.primaryForeground ?? '';

    // All core surfaces are required for a coherent skin; bail out
    // (no skin) rather than apply a half-themed palette.
    if ( !editorBackground || !editorForeground || !windowBackground || !primaryForeground ) {
      this.productSurfaceSubject.next( null );
      return;
    }

    this.productSurfaceSubject.next( {
      editorBackground,
      editorForeground,
      windowBackground,
      surface: tokens.surface ?? windowBackground,
      inputBackground: tokens.inputBackground ?? windowBackground,
      primaryForeground,
      secondaryForeground: tokens.secondaryForeground ?? '',
      hover: tokens.hover ?? '',
      pluginPrimary: tokens.primaryColor ?? '',
      pluginSecondary: tokens.secondaryColor ?? '',
      tertiaryColor: tokens.tertiaryColor ?? '',
      appearance,
      error: tokens.error ?? '',
      warning: tokens.warning ?? '',
      info: tokens.info ?? '',
      success: tokens.success ?? '',
    } );
  }

  initSideNavClosed( sideNavClosePreferred: boolean ) {
    const sideNavClosedStorageItem = sideNavClosePreferred ? 'true' : 'false';
    this.persistThemeItem( 'sideNavClosed', sideNavClosedStorageItem );
    this.sideNavClosedSubject.next( sideNavClosedStorageItem );
  }

  toggleSideNav() {
    const sideNavClosedStorageItem = localStorage.getItem( 'sideNavClosed' ) ?
        localStorage.getItem( 'sideNavClosed' ) : sessionStorage.getItem( 'sideNavClosed' );
    const toggledSideNavClosed = sideNavClosedStorageItem === 'true' ? 'false' : 'true';
    this.persistThemeItem( 'sideNavClosed', toggledSideNavClosed );
    this.sideNavClosedSubject.next( toggledSideNavClosed );
  }

  selectDarkLightByCSS( light: any, dark: any ) {
    return window.matchMedia && window.matchMedia( '(prefers-color-scheme: dark)' ).matches ? dark : light;
  }

  logout() {
    if ( localStorage.getItem( 'currentUser' ) ) {
      localStorage.removeItem( 'darkLightSetting' );
      localStorage.removeItem( 'themeColorSetting' );
      localStorage.removeItem( 'themeDesignSetting' );
      localStorage.removeItem( 'sideNavClosed' );
      localStorage.removeItem( 'productTheme' );
    }
    sessionStorage.removeItem( 'darkLightSetting' );
    sessionStorage.removeItem( 'themeColorSetting' );
    sessionStorage.removeItem( 'themeDesignSetting' );
    sessionStorage.removeItem( 'sideNavClosed' );
    sessionStorage.removeItem( 'productTheme' );

    this.darkLightSettingSubject.next( DarkLightSettings.auto );
    this.themeColorSettingSubject.next( DEFAULT_THEME );
    this.themeDesignSettingSubject.next( DEFAULT_THEME );
    this.sideNavClosedSubject.next( 'false' );
    this.productThemeSubject.next( null );
    this.productSurfaceSubject.next( null );
  }

  fillDefaultColors( shift: number = 0 ) {
    const colorList = [];
    Object.values( ThemeColorSettings ).forEach( themeColorSetting => colorList.push( themeColorSetting ) );

    for ( let i = 0; i < shift; i++ ) {
      const color = colorList.shift();
      colorList.push( color );
      colorList.pop();
    }
    return colorList;
  }
}
